import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login, loginWithGoogle, requestMagicLink } from "@/services/auth";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/context/ToastContext";

type Tab = "password" | "magic" | "google";

export default function LoginPage() {
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const [tab, setTab] = useState<Tab>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err: any) {
      showToast("error", err.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await requestMagicLink(email);
      showToast("success", "Login link sent! Check your inbox.");
    } catch (err: any) {
      showToast("error", err.detail || "Failed to send login link");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100">
      <div className="max-w-sm mx-auto pt-20 px-6 pb-12">
        <h1 className="text-2xl font-bold mb-6 text-slate-900 dark:text-slate-100">
          Sign in to Jobs Bot
        </h1>

        <div role="tablist" className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
          {(["password", "magic", "google"] as Tab[]).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={[
                "px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors duration-200",
                tab === t
                  ? "border-accent text-accent"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
              ].join(" ")}
            >
              {t === "password" ? "Email & Password" : t === "magic" ? "Magic Link" : "Google"}
            </button>
          ))}
        </div>

        {tab === "password" && (
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors duration-200"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 block w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors duration-200"
              />
            </label>
            <Button type="submit" variant="primary" loading={loading} className="w-full">
              Sign in
            </Button>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              <a href="/register" className="text-accent hover:text-accent-hover">
                Create an account
              </a>
            </p>
          </form>
        )}

        {tab === "magic" && (
          <form onSubmit={handleMagicLink} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors duration-200"
              />
            </label>
            <Button type="submit" variant="primary" loading={loading} className="w-full">
              Send login link
            </Button>
          </form>
        )}

        {tab === "google" && (
          <div className="pt-2">
            <Button
              variant="secondary"
              onClick={loginWithGoogle}
              aria-label="Sign in with Google"
              className="w-full"
            >
              Sign in with Google
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
